/************************************************************************************

* UTILITY FUNCTIONS

*************************************************************************************/
class SectionId {
	static #separator = '__'

	// for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
	static parseId(qualifiedSectionId) {
		return qualifiedSectionId.split(SectionId.#separator)[0]
	}

	// for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
	static parseSectionName(qualifiedSectionId) {
		return qualifiedSectionId.split(SectionId.#separator)[1]
	}

	// for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
	static getIdForSection(sectionId, sectionName) {
		return `${sectionId}${SectionId.#separator}${sectionName}`
	}
}

class HTMLUpdateUtility {
	/**
	 * Used to swap an HTML node with a new node.
	 * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
	 *
	 * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
	 */
	static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
		preProcessCallbacks?.forEach((callback) => callback(newContent))

		const newNodeWrapper = document.createElement('div')
		HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML)
		const newNode = newNodeWrapper.firstChild

		// dedupe IDs
		const uniqueKey = Date.now()
		oldNode.querySelectorAll('[id], [form]').forEach((element) => {
			element.id && (element.id = `${element.id}-${uniqueKey}`)
			element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`)
		})

		oldNode.parentNode.insertBefore(newNode, oldNode)
		oldNode.style.display = 'none'

		postProcessCallbacks?.forEach((callback) => callback(newNode))

		setTimeout(() => oldNode.remove(), 500)
	}

	// Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
	static setInnerHTML(element, html) {
		element.innerHTML = html
		element.querySelectorAll('script').forEach((oldScriptTag) => {
			const newScriptTag = document.createElement('script')
			Array.from(oldScriptTag.attributes).forEach((attribute) => {
				newScriptTag.setAttribute(attribute.name, attribute.value)
			})
			newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML))
			oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag)
		})
	}
}

function l_getFocusableElements(container) {
	return Array.from(
		container.querySelectorAll(
			"summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
		)
	).filter((element) => !element.closest('[aria-hidden="true"]'))
}

function l_disableFocusableElements(container) {
	const focusableElements = l_getFocusableElements(container)
	focusableElements.forEach((element) => {
		element.setAttribute('tabindex', '-1')
		element.setAttribute('aria-disabled', 'true')
	})
}

function l_enableFocusableElements(container) {
	const focusableElements = l_getFocusableElements(container)

	focusableElements.forEach((element) => {
		element.removeAttribute('tabindex')
		element.removeAttribute('aria-disabled')
	})
}

function l_throttle(fn, delay) {
	let lastCall = 0

	return function (...args) {
		const now = new Date().getTime()
		if (now - lastCall < delay) {
			return
		}
		lastCall = now

		return fn(...args)
	}
}

function l_addEventDelegate({
	context = document.documentElement,
	event = 'click',
	selector,
	handler,
	capture = false,
}) {
	const listener = function (e) {
		// loop parent nodes from the target to the delegation node
		for (let target = e.target; target && target !== this; target = target.parentNode) {
			if (target.matches(selector)) {
				handler.call(target, e, target)
				break
			}
		}
	}
	context.addEventListener(event, listener, capture)
	return () => {
		context.removeEventListener(event, listener, capture)
	}
}

function l_debounce(fn, wait) {
	let t

	return (...args) => {
		clearTimeout(t)
		t = setTimeout(() => fn.apply(this, args), wait)
	}
}

function l_fetchConfig(type = 'json') {
	return {
		method: 'POST',
		headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
	}
}

function l_getSectionDOM(html, selector = '.shopify-section') {
	return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)
}

function l_updateHeaderCSSVariables() {
	var headerSection = headerSection || document.querySelector('.section-header')
	var announcementBarSection = announcementBarSection || document.querySelector('.section-announcement-bar')

	document.documentElement.style.setProperty('--l-viewport-height', `${visualViewport?.height ?? window.innerHeight}px`)
	document.documentElement.style.setProperty('--l-header-height', `${headerSection?.scrollHeight || 0}px`)
	document.documentElement.style.setProperty(
		'--l-announcement-bar-height',
		`${announcementBarSection?.scrollHeight || 0}px`
	)
}

const trapFocusHandlers = {}

function l_trapFocus(container, defaultElementToFocus) {
	const elements = l_getFocusableElements(container)
	const first = elements[0]
	const last = elements[elements.length - 1]
	const elementToFocus = defaultElementToFocus || first

	l_removeTrapFocus()

	trapFocusHandlers.focusin = (event) => {
		if (event.target !== container && event.target !== last && event.target !== first) return

		document.addEventListener('keydown', trapFocusHandlers.keydown)
	}

	trapFocusHandlers.focusout = function () {
		document.removeEventListener('keydown', trapFocusHandlers.keydown)
	}

	trapFocusHandlers.keydown = function (event) {
		if (event.code.toUpperCase() !== 'TAB') return // If not TAB key
		// On the last focusable element and tab forward, focus the first element.
		if (event.target === last && !event.shiftKey) {
			event.preventDefault()
			first.focus()
		}

		//  On the first focusable element and tab backward, focus the last element.
		if ((event.target === container || event.target === first) && event.shiftKey) {
			event.preventDefault()
			last.focus()
		}
	}

	document.addEventListener('focusout', trapFocusHandlers.focusout)
	document.addEventListener('focusin', trapFocusHandlers.focusin)

	elementToFocus.focus()

	if (
		elementToFocus.tagName === 'INPUT' &&
		['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
		elementToFocus.value
	) {
		elementToFocus.setSelectionRange(0, elementToFocus.value.length)
	}
}

function l_removeTrapFocus(elementToFocus = null) {
	document.removeEventListener('focusin', trapFocusHandlers.focusin)
	document.removeEventListener('focusout', trapFocusHandlers.focusout)
	document.removeEventListener('keydown', trapFocusHandlers.keydown)

	if (elementToFocus) elementToFocus.focus()
}

function l_pauseAllMedia() {
	document.querySelectorAll('.js-youtube').forEach((video) => {
		video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*')
	})
	document.querySelectorAll('.js-vimeo').forEach((video) => {
		video.contentWindow.postMessage('{"method":"pause"}', '*')
	})
	document.querySelectorAll('video').forEach((video) => video.pause())
	document.querySelectorAll('product-model').forEach((model) => {
		if (model.modelViewerUI) model.modelViewerUI.pause()
	})
	document.querySelectorAll('l-media-video').forEach((video) => {
		video.player.pause()
	})
}

function l_formatMoney(cents, format = '') {
	let value = ''
	const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/
	const formatString = format || theme.money_format || '${{amount}}'

	if (typeof cents == 'string') {
		cents = cents.replace('.', '')
	}

	function defaultOption(opt, def) {
		return typeof opt == 'undefined' ? def : opt
	}

	function formatWithDelimiters(number, precision, thousands, decimal) {
		precision = defaultOption(precision, 2)
		thousands = defaultOption(thousands, ',')
		decimal = defaultOption(decimal, '.')

		if (isNaN(number) || number == null) {
			return 0
		}

		number = (number / 100.0).toFixed(precision)

		var parts = number.split('.'),
			dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands),
			cents = parts[1] ? decimal + parts[1] : ''

		return dollars + cents
	}

	switch (formatString.match(placeholderRegex)[1]) {
		case 'amount':
			value = formatWithDelimiters(cents, 2)
			break
		case 'amount_no_decimals':
			value = formatWithDelimiters(cents, 0, ' ', '.')
			break
		case 'amount_with_comma_separators':
			value = formatWithDelimiters(cents, 2, '.', '.')
			break
		case 'amount_no_decimals_with_comma_separators':
			value = formatWithDelimiters(cents, 0, '.', '.')
			break
	}

	return formatString.replace(placeholderRegex, value)
}

function l_lockBodyScroll(elementName) {
	document.documentElement.classList.add('js-block-page')

	if (elementName) {
		document.documentElement.classList.add(`js-block-page-${elementName}`)
	}
}

function l_unLockBodyScroll(elementName) {
	if (elementName) {
		document.documentElement.classList.remove(`js-block-page-${elementName}`)

		const isExistAnotherBlockedClass = Array.from(document.documentElement.classList).some((cls) =>
			cls.startsWith('js-block-page-')
		)

		!isExistAnotherBlockedClass && document.documentElement.classList.remove('js-block-page')
	} else {
		document.documentElement.classList.remove('js-block-page')
	}
}

function l_setupVariablesBeforeOpenModal() {
	const announcementBarSection = document.getElementById('announcementBar')
	const headerSection = document.getElementById('header')

	document.documentElement.style.setProperty('--l-viewport-height', `${visualViewport?.height ?? window.innerHeight}px`)
	document.documentElement.style.setProperty('--l-header-height', `${headerSection?.offsetHeight || 0}px`)
	document.documentElement.style.setProperty(
		'--l-announcement-bar-height',
		`${announcementBarSection?.offsetHeight || 0}px`
	)
}

function l_isElementInViewport(el) {
	const rect = el.getBoundingClientRect()
	return (
		rect.top >= 0 &&
		rect.left >= 0 &&
		rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
		rect.right <= (window.innerWidth || document.documentElement.clientWidth)
	)
}

async function refreshCartDrawer() {
	if (!window.Cart || typeof window.Cart.refresh !== 'function') {
		console.warn('Cart store is not ready yet')
		return null
	}

	try {
		return await window.Cart.refresh()
	} catch (error) {
		console.error('Error refreshing theme cart store:', error)
		return null
	}
}

const L_ATC = {
	cartDrawer: null,
	handleConfigWithFormEl(formEl) {
		const config = l_fetchConfig('javascript')
		config.headers['X-Requested-With'] = 'XMLHttpRequest'
		delete config.headers['Content-Type']

		const formData = new FormData(formEl)

		config.body = formData

		return config
	},
	handleConfigWithVariantId(addingData) {
		// Setup config object
		const config = l_fetchConfig('javascript')
		config.headers['X-Requested-With'] = 'XMLHttpRequest'
		delete config.headers['Content-Type']

		// Setup form data
		const formData = new FormData()

		addingData.forEach((item, index0) => {
			formData.append(`items[${index0}][quantity]`, item.quantity)
			formData.append(`items[${index0}][id]`, item.id)
		})

		// Insert formData into config
		config.body = formData

		return config
	},
	addToCart(config, id = '234na08fa3') {
		return fetch(window.theme.routes.cart_add_url, config)
			.then((response) => response.json())
			.then((response) => {
				// ATC: Handle error case
				if (response.status) {
					document.dispatchEvent(new CustomEvent('l:cart:error', { detail: { id, description: response.description } }))

					return
				}

				// Redirect to cart page if cart-drawer equal to null
				// if (!this.cartDrawer) this.cartDrawer = document.getElementById('CartDrawer')
				// if (!this.cartDrawer) {
				// 	// window.location = window.theme.routes.cart_url

				// 	return
				// }

				// Publish added to cart event
				fetch('/cart.js')
					.then((cartResponse) => cartResponse.json())
					.then(async (cartData) => {

						// Publish added to cart event
						document.dispatchEvent(new CustomEvent('l:cart:updated', { bubbles: true, detail: { id, response, cartData } }));

						await refreshCartDrawer(); 
						// Open cart drawer after add to cart
const cartDrawerEl = document.getElementById('modal-cart');
if (cartDrawerEl) {
  cartDrawerEl.open();
}


						
					})
					.catch((error) => {
						console.error('Error fetching cart data:', error);
					});
			})
			.catch((error) => {
				console.error(error)
			})
	},
}

/************************************************************************************

* GENERAL COMPONENTS

*************************************************************************************/

/********************

* [COMPONENT]: l-collapsible-tab

*********************/

class LTACollapsibleTab extends HTMLElement {
	constructor() {
		super()
	}

	connectedCallback() {
		// Elements
		this.trigger = this.querySelector('[data-trigger]')
		this.content = this.querySelector('[data-content]')

		// Data
		this.expandedClass = 'is-expanded'
		this.collapsedClass = 'is-collapsed'
		this.collapsedHeight = '0px'
		this.oneAtATime = false
		this.isScrollIntoView = this.dataset.scrollIntoView === 'true'

		this.transitionendEvent = this.transitionendEventName()

		// Setup default behavior / data
		this.dataset.oneOpen === 'true' && (this.oneAtATime = true)

		if (this.getAttribute('open') === 'true') {
			this.selected = true
			this.classList.add(this.expandedClass)
			this.setExpandedAria()
		} else {
			this.content.style.height = this.collapsedHeight
			this.classList.add(this.collapsedClass)
			this.setCollapsedAria()
			l_disableFocusableElements(this.content)
		}

		this.content.removeAttribute('hidden')

		// Events
		this.trigger.addEventListener('click', (e) => this.toggle(e))
	}

	toggle(e) {
		e.preventDefault()

		this.selected ? this.close() : this.open()

		/* Close another tab and open only the current tab. */
		if (this.selected && this.oneAtATime) {
			const tabsContainer = this.closest('[data-collapsible-wrapper]')
			const collapsibleTabs = tabsContainer?.querySelectorAll('l-collapsible-tab')

			collapsibleTabs?.length &&
				collapsibleTabs.forEach((tab) => {
					tab !== this && tab.selected && tabsContainer !== tab && tab.close()
				})
		}
	}

	open() {
		this.selected = true
		this.expand()
		this.setAttribute('open', true)
		l_enableFocusableElements(this.content)
	}

	close() {
		this.selected = false
		this.collapse()
		this.removeAttribute('open')
		l_disableFocusableElements(this.content)
	}

	expand() {
		const onTransitionEnd = ((event) => {
			if (event.target === this.content) {
				this.content.removeEventListener(this.transitionendEvent, onTransitionEnd)

				this.isOpen &&
					requestAnimationFrame(() => {
						this.content.style.transition = '0'
						this.content.style.height = 'auto'

						requestAnimationFrame(() => {
							this.content.style.height = null
							this.content.style.transition = null
							this.setExpandedAria()
						})
					})
			}
		}).bind(this)

		this.content.addEventListener(this.transitionendEvent, onTransitionEnd)
		this.isOpen = true
		this.classList.add(this.expandedClass)
		this.classList.remove(this.collapsedClass)
		this.content.style.height = this.content.scrollHeight + 'px'
	}

	collapse() {
		const onTransitionEnd = ((event) => {
			if (event.target === this.content) {
				this.content.removeEventListener(this.transitionendEvent, onTransitionEnd)

				if (!this.isOpen) {
					this.setCollapsedAria()
					this.classList.add(this.collapsedClass)
				}
			}
		}).bind(this)

		this.content.addEventListener(this.transitionendEvent, onTransitionEnd)
		this.isOpen = false
		this.classList.remove(this.expandedClass)

		requestAnimationFrame(() => {
			this.content.style.transition = '0'
			this.content.style.height = this.content.scrollHeight + 'px'

			requestAnimationFrame(() => {
				this.content.style.transition = null
				this.content.style.height = this.collapsedHeight
			})
		})

		if (this.isScrollIntoView) {
			l_debounce(() => {
				this.scrollIntoView({ behavior: 'smooth', block: 'center' })
			}, 400)()
		}
	}

	destroy() {
		this.trigger && this.trigger.removeEventListener('click', (e) => this.toggle(e))
		this.content && this.content.removeAttribute('aria-hidden')
		this.content && (this.content.style.height = 'auto')

		this.classList.remove(this.expandedClass, this.collapsedClass)
		this.removeAttribute('open')
	}

	transitionendEventName() {
		let eventName,
			testElement = document.createElement('div'),
			eventNames = {
				transition: 'transitionend',
				OTransition: 'otransitionend',
				MozTransition: 'transitionend',
				WebkitTransition: 'webkitTransitionEnd',
			}

		for (eventName in eventNames) {
			if (eventNames.hasOwnProperty(eventName) && testElement.style[eventName] !== false) {
				return eventNames[eventName]
			}
		}
	}

	setExpandedAria() {
		this.trigger.setAttribute('aria-expanded', 'true')
		this.content.removeAttribute('aria-hidden')
		this.content.removeAttribute('tabindex')
	}

	setCollapsedAria() {
		this.trigger.setAttribute('aria-expanded', 'false')
		this.content.setAttribute('aria-hidden', 'true')
		this.content.setAttribute('tabindex', '-1')
	}
}

customElements.define('l-collapsible-tab', LTACollapsibleTab)

/********************

* [COMPONENT]: l-swiper-carousel

*********************/
class LTASwiperCarousel extends HTMLElement {
	constructor() {
		super()
	}

	connectedCallback() {
		// Check if Swiper is already loaded
		if (window.Swiper) {
			this.setupCarousel()

			return
		}

		document.addEventListener('l:vendor:loaded', () => {
			this.setupCarousel()
		})
	}

	setupCarousel() {
		this.carouselConfig = JSON.parse(this.querySelector('[data-carousel-config]').textContent)

		if (!this.carouselConfig) return

		this.swiperSlideEls = this.querySelectorAll('.swiper-slide')

		this.classList.remove('is-placeholder')
		this.swiperSlideEls.forEach((swiperSlide) => swiperSlide.classList.remove('l-hidden'))

		this.swiperCarousel = new window.Swiper(this, this.carouselConfig)

		Shopify.designMode && this.designModeScroll()
	}

	designModeScroll() {
		document.addEventListener('shopify:block:select', (event) => {
			const targetEl = event.target

			this.swiperSlideEls.forEach((swiperSlide, index0) => {
				if (targetEl == swiperSlide) this.slideTo(index0)
			})
		})
	}

	slideTo(index = 0) {
		this.swiperCarousel.slideTo(index)
	}
}

customElements.define('l-swiper-carousel', LTASwiperCarousel)

/********************

* [COMPONENT]: l-comparison-images

*********************/
class LTAComparisonImages extends HTMLElement {
	constructor() {
		super()

		this.inputRange = this.querySelector('input[type="range"]')

		this.inputRange.addEventListener('input', () => {
			this.style.setProperty('--l-percent', `${this.inputRange.value}%`)
		})
	}
}

customElements.define('l-comparison-images', LTAComparisonImages)

/********************

* [COMPONENT]: l-media-video

*********************/
class LTAMediaVideo extends HTMLElement {
	constructor() {
		super()
	}

	connectedCallback() {
		document.addEventListener('l:vendor:loaded', () => {
			this.initPlayer()
		})
	}

	initPlayer() {
		this.videoEl = this.querySelector('[data-video]')

		if (!this.videoEl) return

		const configScrip = this.querySelector('script[type="application/json"]')

		if (configScrip) {
			this.config = JSON.parse(configScrip.textContent)
		}

		this.player = new Plyr(this.videoEl, this.config || {})
	}
}

customElements.define('l-media-video', LTAMediaVideo)

/********************

* [COMPONENT]: l-select-component

*********************/
class LTASelectComponent extends HTMLElement {
	constructor() {
		super()
	}

	connectedCallback() {
		// Elements
		this.triggerButtonEl = this.querySelector('[data-button-trigger]')
		this.triggerButtonContentEl = this.querySelector('[data-button-content]') || this.triggerButtonEl
		this.selectModalEl = this.querySelector('[data-modal]')
		this.selectModalContainerEl = this.querySelector('[data-modal-container]')
		this.modalCloseEls = this.querySelectorAll('[data-modal-close]')
		this.modalName = this.dataset.modalName || 'select'

		this.nativeSelectEl = this.querySelector('select')
		this.selectOptionEls = this.querySelectorAll('[data-option-value]')

		// Open / Close events
		this.triggerButtonEl.addEventListener('click', () => {
			const isOpen = this.triggerButtonEl.classList.contains('is-active')

			isOpen ? this.close() : this.open()
		})

		this.modalCloseEls.forEach((modalCloseEl) => {
			modalCloseEl.addEventListener('click', () => {
				this.close()
			})
		})

		// Update default events
		this.changeEvent = new Event('change', { bubbles: true })
		this.setDefaultValue()

		this.nativeSelectEl.addEventListener('change', () => {
			this.setDefaultValue()
		})

		// Close events
		this.selectOptionEls.forEach((selectOptionEl) => {
			selectOptionEl.addEventListener('click', () => {
				this.nativeSelectEl.value = selectOptionEl.dataset.optionValue
				this.nativeSelectEl.dispatchEvent(this.changeEvent)

				this.close()
			})
		})
	}

	open() {
		const isMobile = window.matchMedia('(max-width: 990px)').matches

		if (isMobile) l_lockBodyScroll(this.modalName)

		this.triggerButtonEl.classList.add('is-active')
		this.selectModalEl.classList.remove('l-hidden')

		requestAnimationFrame(() => {
			this.selectModalEl.classList.add('is-active')
		})

		l_trapFocus(this.selectModalContainerEl, this.triggerButtonEl)

		// Set global events
		l_debounce(() => {
			window.addEventListener('click', this.closeOutsideOnClick.bind(this))
			window.addEventListener('keyup', this.closeOutsideOnEscape.bind(this))
		}, 200)()
	}

	close() {
		if (!this.selectModalEl.classList.contains('is-active')) return

		const isMobile = window.matchMedia('(max-width: 990px)').matches

		if (isMobile) l_unLockBodyScroll(this.modalName)

		this.triggerButtonEl.classList.remove('is-active')
		this.selectModalEl.classList.remove('is-active')

		window.l_debounce(() => {
			this.selectModalEl.classList.add('l-hidden')
		}, 400)()

		l_removeTrapFocus(this.triggerButtonEl)

		// Remove global events
		window.removeEventListener('click', this.closeOutsideOnClick.bind(this))
		window.removeEventListener('keyup', this.closeOutsideOnEscape.bind(this))
	}

	closeOutsideOnClick(event) {
		const target = event.target
		const isOutsideClick =
			this.triggerButtonEl != target && !this.triggerButtonEl.contains(target) && !this.selectModalEl.contains(target)

		if (isOutsideClick) this.close()
	}

	closeOutsideOnEscape(event) {
		if (event.code.toUpperCase() === 'ESCAPE') {
			this.close()
		}
	}

	setDefaultValue() {
		const selectedValue = this.nativeSelectEl.value
		const prevSelectedOption = Array.from(this.nativeSelectEl.options).find((option) => option.getAttribute('selected'))

		if (!prevSelectedOption) return

		const currentSelectedOption = this.nativeSelectEl.selectedOptions[0]

		prevSelectedOption.removeAttribute('selected')
		prevSelectedOption.classList.remove('is-selected')

		if (currentSelectedOption) {
			currentSelectedOption.setAttribute('selected', 'selected')
			currentSelectedOption.classList.add('is-selected')
		}

		this.selectOptionEls.forEach((selectOptionEl) => {
			const currentOptionValue = selectOptionEl.dataset.optionValue
			selectOptionEl.classList.toggle('is-selected', currentOptionValue == selectedValue)

			if (currentOptionValue == selectedValue) {
				this.triggerButtonContentEl.innerHTML = selectOptionEl.innerHTML
			}
		})
	}
}

customElements.define('l-select-component', LTASelectComponent)

/********************

* [COMPONENT]: l-country-localization

*********************/
class LTACountryLocalization extends LTASelectComponent {
	connectedCallback() {
		super.connectedCallback()

		this.selectEl = this.querySelector('[name="country_code"]')
		this.formEl = this.closest('form') || this.querySelector('form')

		l_addEventDelegate({
			context: this,
			selector: '[data-option-value]',
			handler: (e, selectedCountryEl) => {
				e.preventDefault()

				const selectedCountryISO = selectedCountryEl.dataset.optionValue

				this.selectEl.value = selectedCountryISO

				if (this.formEl) this.formEl.submit()
			},
		})
	}
}

customElements.define('l-country-localization', LTACountryLocalization)

class LTACountryLocalizationV2 extends HTMLElement {
	constructor() {
		super()
	}

	connectedCallback() {
		this.selectEl = this.querySelector('[name="country_code"]')
		this.formEl = this.closest('form') || this.querySelector('form')

		l_addEventDelegate({
			context: this,
			selector: '[data-option-value]',
			handler: (e, selectedCountryEl) => {
				e.preventDefault()

				const selectedCountryISO = selectedCountryEl.dataset.optionValue

				this.selectEl.value = selectedCountryISO

				if (this.formEl) this.formEl.submit()
			},
		})
	}
}

customElements.define('l-country-localization-v2', LTACountryLocalizationV2)

/********************

* [COMPONENT]: l-drawer

*********************/
class LTADrawer extends HTMLElement {
	constructor() {
		super()
	}

	connectedCallback() {
		this.setupDrawer()
	}

	setupDrawer() {
		// Elements
		this.triggerEls = document.querySelectorAll(this.dataset.triggerSelector)
		this.closeBtnEls = this.querySelectorAll('[data-close]')
		this.drawerInnerEl = this.querySelector('[data-drawer-inner]')
		this.triggerEl = null

		// Data
		this.modalName = this.dataset.modalName || 'drawer'

		// Open events
		this.triggerEls.forEach((triggerEl) => {
			triggerEl.addEventListener('click', (event) => {
				event.preventDefault()

				this.drawer_open(triggerEl)
			})
		})

		// Close events
		this.closeBtnEls.forEach((closeBtnEl) => {
			closeBtnEl.addEventListener('click', () => {
				this.drawer_close()
			})
		})
	}

	drawer_open(triggerEl) {
		l_setupVariablesBeforeOpenModal()
		l_lockBodyScroll(this.modalName)

		// Open drawer
		this.classList.remove('l-hidden')

		requestAnimationFrame(() => {
			this.classList.add('is-active')
		})

		// Setup focus trap on main content
		if (triggerEl) {
			this.triggerEl = triggerEl
			l_trapFocus(this.drawerInnerEl)
		}

		// Set global events
		window.addEventListener('click', this.drawer_closeOutsideOnClick.bind(this))
		window.addEventListener('keyup', this.drawer_closeOutsideOnEscape.bind(this))
	}

	drawer_close() {
		l_unLockBodyScroll(this.modalName)

		// Close drawer
		this.classList.remove('is-active')

		window.l_debounce(() => {
			this.classList.add('l-hidden')
		}, 400)()

		// Remove focus trap
		if (this.triggerEl) {
			l_removeTrapFocus(this.triggerEl)
			this.triggerEl = null
		}

		// Remove global events
		window.removeEventListener('click', this.drawer_closeOutsideOnClick)
		window.removeEventListener('keyup', this.drawer_closeOutsideOnEscape)
	}

	drawer_closeOutsideOnClick(event) {
		const target = event.target
		const isOutsideClick =
			!this.drawerInnerEl.contains(target) && !Array.from(this.triggerEls).some((el) => el.contains(target))

		if (isOutsideClick) this.drawer_close()
	}

	drawer_closeOutsideOnEscape(event) {
		if (event.code.toUpperCase() === 'ESCAPE') {
			this.drawer_close()
		}
	}
}

customElements.define('l-drawer', LTADrawer)

/********************

* [COMPONENT]: l-product-card

*********************/

class LTAProductCard extends HTMLElement {
	constructor() {
		super()
	}

	connectedCallback() {
		// Elements
		this.atcBtnEl = this.querySelector('[data-atc]')

		// Data
		this.selectedVariantID = this.dataset.defaultVariantId

		// Events
		if (this.atcBtnEl) {
			this.atcBtnEl.addEventListener('click', this.onAddToCart.bind(this))
		}
	}

	onAddToCart() {
		// Enable loading
		this.atcBtnEl.classList.add('is-loading')

		// Setup form data
		const config = L_ATC.handleConfigWithVariantId([{ id: this.selectedVariantID, quantity: 1 }])

		// Add selected variant to cart
		L_ATC.addToCart(config, 'l-product-upsell-card').finally(() => {
			this.atcBtnEl.classList.remove('is-loading')
		})
	}
}

customElements.define('l-product-card', LTAProductCard)

/********************

* [COMPONENT]: l-product-card

*********************/

class LTAVideo extends HTMLElement {
	constructor() {
		super()
	}

	connectedCallback() {
		// Elements
		this.mediaEl = this.querySelector('[data-media]')
		this.watchVideoBtn = this.querySelector('[data-watch-video]')
		this.videoEl = null
		this.videoWrapperEl = null

		// Data
		this.isVideoLoaded = false

		// Events
		if (this.mediaEl && this.watchVideoBtn) {
			this.watchVideoBtn.addEventListener('click', this.onPlayVideo.bind(this))
		}
	}

	onPlayVideo() {
		// Initial video
		if (!this.isVideoLoaded) {
			const templateEl = this.mediaEl.querySelector('template')

			if (!templateEl) return

			this.watchVideoBtn.classList.add('is-loading')
			l_debounce(() => {
				this.watchVideoBtn.classList.remove('is-loading')
			}, 2000)()

			// Stop other videos
			// l_pauseAllMedia()

			// Update HTML
			const videoTemplate = templateEl.content.cloneNode(true)
			this.videoEl = videoTemplate.querySelector('video')
			this.mediaEl.appendChild(videoTemplate)
			this.videoEl?.play?.()
			this.videoEl?.focus?.()

			l_debounce(() => {
				document.querySelectorAll('.js-youtube').forEach((video) => {
					video.contentWindow.postMessage('{"event":"command","func":"' + 'playVideo' + '","args":""}', '*')
				})
			}, 1000)()

			this.classList.add('l-video-playing')

			// Query video wrapper element
			this.videoWrapperEl = this.querySelector('[data-video-wrapper]')

			// Update data
			this.isVideoLoaded = true

			// Setup events
			this.setupVideoEvents()

			return
		}

		// Video already loaded case
		l_pauseAllMedia()

		if (this.videoWrapperEl) this.videoWrapperEl.classList.remove('l-hidden')
		this.classList.add('l-video-playing')
		this.videoEl.play()
		this.videoEl.focus()
	}

	setupVideoEvents() {
		if (!this.videoEl) return

		this.videoEl.addEventListener('ended', () => {
			if (this.videoWrapperEl) this.videoWrapperEl.classList.add('l-hidden')
			this.classList.remove('l-video-playing')
			this.videoEl.pause()
			this.watchVideoBtn.focus()
		})
	}
}

customElements.define('l-video-media', LTAVideo)

/************************************************************************************

* SECTIONS

*************************************************************************************/
class LTAProductTabs extends HTMLElement {
	constructor() {
		super()
	}

	connectedCallback() {
		// Elements
		this.tabEls = this.querySelectorAll('[data-tab]')
		this.boxEls = this.querySelectorAll('[data-box]')

		// Data

		// Events
		this.tabEls.forEach((tabEl) => {
			tabEl.addEventListener('click', () => {
				this.tabEls.forEach((tab) => tab.classList.toggle('is-active', tab === tabEl))

				this.boxEls.forEach((boxEl) => {
					boxEl.classList.toggle('l-hidden', boxEl.dataset.box !== tabEl.dataset.tab)
				})
			})
		})
	}
}

customElements.define('l-products-tabs', LTAProductTabs)

/************************************************************************************

* CUSTOMIZE FUNCTIONALITIES

*************************************************************************************/
window.addEventListener('DOMContentLoaded', () => {
	/**********************************
  ADD CLASSES TO RTE TABLES
  **********************************/
	function addClassesToRteTables() {
		document.querySelectorAll('.l-block-rte table').forEach((table) => {
			const wrapper = document.createElement('div')
			wrapper.classList.add('l-rte-table', 'l-overflow-x-auto', 'l-scrollbar--hidden')
			table.parentNode.insertBefore(wrapper, table)
			wrapper.appendChild(table)
		})
	}

	function openHubsportByLinks() {
		const hubsportLinkEls = document.querySelectorAll('[href="#open-hubspot"]')

		hubsportLinkEls.forEach((link) => {
			link.addEventListener('click', (event) => {
				event.preventDefault()

				window?.HubSpotConversations?.widget?.open()
			})
		})
	}

	// Initialize
	addClassesToRteTables()
	openHubsportByLinks()
})

